import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const featured = {
  id: "01",
  client: "Fi Money × Sima Taparia",
  type: "Campaign strategy · Influencer marketing",
  title: "India's First Mutual Fund Matchmaker — a national celebrity campaign, signed to shipped in 7 days.",
  challenge:
    "Fi Money was launching curated mutual fund baskets into a category defined by jargon, complexity, and low trust among millennials. They needed to break through without sounding like a traditional finance brand.",
  idea:
    "We turned India's most recognisable matchmaker into India's first Mutual Fund Matchmaker — casting Sima Taparia, star of Indian Matchmaking, as the face of Fi Money's mutual fund baskets. The campaign moved as fast as the idea was sharp: from signing the celebrity to shipping the finished film, her first-ever LinkedIn profile, and a platform-first launch, in seven days.",
  role:
    "The matchmaking parallel came from a simple observation — choosing a mutual fund feels like being matched: different risk appetites, different expectations, everyone looking for the perfect fit. End-to-end campaign ownership — social strategy, influencer coordination, shoot execution, PR alignment, stakeholder management, and budget approvals. Delivered in one week.",
  impact: [
    "Celebrity signed and contracted in 72 hours; full campaign — film, Sima's first-ever LinkedIn profile, platform-first launch — delivered in under a week.",
    "Timed precisely to the Indian Matchmaking Season 2 Netflix release, riding peak cultural conversation.",
    "The film crossed 3M views; more importantly, it turned a jargon-heavy financial product into a culturally fluent, trust-building idea.",
  ],
  quote: "The storytelling wasn't separate from the product — it explained it.",
};

const cases = [
  {
    id: "02",
    client: "Kore (D2C Shapewear Brand)",
    type: "Brand identity · Commerce",
    title: "Building Kore's Amazon storefront from zero",
    hero: "https://customer-assets.emergentagent.com/job_shivani-studio/artifacts/eisvwykb_kore-amazon-listing.png",
    challenge:
      "Kore needed a premium Amazon presence for their shapewear brand — a category where imagery does most of the heavy lifting and generic listings don't convert.",
    role:
      "Built the entire Amazon storefront end-to-end: keyword research, product titles, descriptions, A+ content, and brand page design. Produced all product imagery and campaign visuals for the store. Now managing and optimising the listings on an ongoing basis.",
    stats: [
      { v: "0 → Live", l: "Full storefront built from scratch" },
      { v: "Ongoing", l: "Retainer management" },
      { v: "A+ Content", l: "Across every listing" },
      { v: "2 products", l: "Initial listings, scaling up" },
    ],
    quote: "From a brand marketer's lens that's rare to come by.",
    attribution: "Bani Singh · Founder, Kore · Forbes 30u30",
    liveUrl:
      "https://www.amazon.in/KORE-KOMFORT-Detachable-Transparent-Off-Shoulder/dp/B0H1RHR392/",
    liveUrlLabel: "View live store",
  },
  {
    id: "03",
    client: "Fi Neobanking App",
    type: "Social strategy · Community · Real-time marketing",
    title: "Winning IPL season without buying a single ad",
    challenge:
      "Fi needed to build relevance during IPL — the noisiest advertising moment of the year — without the budget to compete with traditional banks.",
    role:
      "Turned interactive online presentations into a two-way engagement tool. Instead of broadcasting, we listened — inviting users to respond and shape content in real time. Built entirely through real-time moment marketing — no shoots, no production budget — with spend focused purely on amplification.",
    stats: [
      { v: "200%", l: "Social follower growth" },
      { v: "10,800+", l: "Hours of user engagement" },
      { v: "Zero", l: "Production cost" },
      { v: "Real-time", l: "Content shaped by live feedback" },
    ],
  },
  {
    id: "04",
    client: "Fi Money",
    type: "ORM system design · Crisis management",
    title: "Keeping a fintech brand credible when things broke",
    challenge:
      "A transaction failure during a product rollout triggered a surge of public complaints while a reliability-focused campaign was live.",
    role:
      "Built a full ORM system — response frameworks, escalation matrices, Sprinklr + OneDirect tooling, cross-functional feedback loops, and team training. Managed the crisis in real time.",
    stats: [
      { v: "0", l: "PR escalations — crisis contained" },
      { v: "Restored", l: "Brand credibility post-crisis" },
      { v: "Scalable", l: "System built to last beyond the crisis" },
      { v: "End-to-end", l: "Strategy through execution" },
    ],
    quote:
      "Trust isn't built when systems work perfectly. It's built when brands take responsibility — visibly, calmly, and consistently.",
  },
];

export default function Work() {
  usePageMeta({
    title: "Work — Shivani | Brand Strategist",
    description: "Case studies in brand strategy, campaign leadership, and social media growth. Including the viral Sima Taparia × Fi Money campaign.",
    ogTitle: "Work — Shivani | Brand Strategist",
    ogDescription: "Case studies in brand strategy and campaign leadership — including 2M+ views in one week.",
  });
  return (
    <div data-testid="work-page">
      {/* INTRO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-12">
        <p className="eyebrow mb-8">(Archive) — Selected case studies · 2022—2026</p>
        <h1
          className="serif-display leading-[0.9]"
          style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
        >
          Selected work.
        </h1>
        <div className="grid md:grid-cols-12 mt-10">
          <p className="md:col-span-7 md:col-start-6 text-base md:text-lg leading-relaxed">
            Four projects that show the range — fintech to D2C, campaign leadership to crisis
            management, AI production to ongoing retainer work. Picked because they moved
            something real: views, sentiment, sign-ups, or trust.
          </p>
        </div>
      </section>

      {/* FEATURED CASE — full width hero */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-20 md:pb-28"
        data-testid="case-featured"
      >
        <article
          style={{
            backgroundColor: "#E8E2D5",
            border: "0.5px solid rgba(26,26,26,0.5)",
          }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_shivani-studio/artifacts/xy3i7ott_sima-campaign-hero.jpg.jpg"
            alt="Fi × Sima Taparia — Mutual Fund Matchmaking campaign"
            className="block w-full h-auto"
            style={{ aspectRatio: "16 / 9", objectFit: "cover", objectPosition: "center" }}
            data-testid="case-featured-hero-image"
          />
          <div className="p-8 md:p-14 lg:p-20">
          <div className="flex flex-wrap items-baseline justify-between gap-4 mb-10">
            <div className="flex items-baseline gap-5">
              <span
                className="serif-display text-3xl md:text-4xl leading-none"
                style={{ color: "rgba(26,26,26,0.45)" }}
              >
                {featured.id}
              </span>
              <span className="text-[11px] font-sans font-medium uppercase tracking-[0.22em]">
                Featured case
              </span>
            </div>
            <span
              className="text-[11px] font-sans font-medium uppercase tracking-[0.22em]"
              style={{ color: "rgba(26,26,26,0.55)" }}
            >
              {featured.type}
            </span>
          </div>

          <h2
            className="serif-display max-w-[20ch] leading-[0.98]"
            style={{ fontSize: "clamp(2.5rem, 6.4vw, 5.5rem)" }}
            data-testid="case-featured-title"
          >
            {featured.title}
          </h2>

          <p
            className="mt-6 eyebrow"
            style={{ color: "rgba(26,26,26,0.55)" }}
          >
            Client · {featured.client}
          </p>

          <div className="rule my-12" style={{ opacity: 0.5 }} />

          <div className="grid md:grid-cols-12 gap-10 md:gap-14">
            <FeaturedBlock label="The challenge" body={featured.challenge} />
            <FeaturedBlock label="The idea" body={featured.idea} />
            <FeaturedBlock label="My role" body={featured.role} className="md:col-span-12" />
          </div>

          {/* YouTube embed — campaign film */}
          <figure className="mt-14" data-testid="case-featured-video">
            <p
              className="text-[11px] font-sans font-medium uppercase tracking-[0.22em] mb-4"
              style={{ color: "rgba(26,26,26,0.55)" }}
            >
              Watch the campaign film
            </p>
            <div
              className="relative w-full overflow-hidden"
              style={{
                aspectRatio: "16 / 9",
                border: "0.5px solid rgba(26,26,26,0.35)",
                backgroundColor: "#1A1A1A",
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/XMwi28towVQ?rel=0&modestbranding=1"
                title="Fi × Sima Taparia — Mutual Fund Matchmaking"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
              />
            </div>
          </figure>

          <div
            className="mt-14 pt-10"
            style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
          >
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              Impact
            </p>
            <ul className="space-y-6 md:space-y-8">
              {featured.impact.map((item, i) => (
                <li key={i} className="flex gap-5 md:gap-6">
                  <span
                    className="serif-display leading-none shrink-0"
                    style={{ fontSize: "1.5rem", color: "rgba(26,26,26,0.35)" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base md:text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <blockquote
            className="mt-14 serif-display leading-[1.1] max-w-[28ch]"
            style={{ fontSize: "clamp(1.5rem, 2.8vw, 2.25rem)" }}
            data-testid="case-featured-quote"
          >
            <span className="serif-italic">&ldquo;</span>
            {featured.quote}
            <span className="serif-italic">&rdquo;</span>
          </blockquote>

          <div className="mt-12">
            <Link
              to="/contact"
              className="font-serif text-xl md:text-2xl editorial-link"
              data-testid="case-featured-link"
            >
              <span className="serif-italic">Ask for the full case study</span> →
            </Link>
          </div>
          </div>
        </article>
      </section>

      {/* OTHER CASES — stacked rows */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        {cases.map((c) => (
          <article
            key={c.id}
            data-testid={`case-${c.id}`}
            className="py-16 md:py-24"
            style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
          >
            {c.hero && (
              <img
                src={c.hero}
                alt={c.title}
                className="block w-full h-auto mb-12 md:mb-16"
                data-testid={`case-${c.id}-hero`}
              />
            )}
            <div className="grid md:grid-cols-12 gap-8 md:gap-12">
            {/* left meta */}
            <div className="md:col-span-4 relative">
              <span
                aria-hidden
                className="serif-display pointer-events-none select-none absolute leading-none"
                style={{
                  fontSize: "clamp(8rem, 16vw, 14rem)",
                  color: "#E8E2D5",
                  top: "-0.18em",
                  left: "-0.04em",
                  zIndex: 0,
                }}
              >
                {c.id}
              </span>
              <div className="relative" style={{ zIndex: 1 }}>
                <div className="flex items-baseline gap-5 mb-6">
                  <span
                    className="text-[11px] font-sans font-medium uppercase tracking-[0.22em]"
                    style={{ color: "rgba(26,26,26,0.55)" }}
                  >
                    Case study {c.id}
                  </span>
                </div>
                <h2 className="serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.02]">
                  {c.title}
                </h2>
                <p
                  className="mt-6 eyebrow"
                  style={{ color: "rgba(26,26,26,0.55)" }}
                >
                  Client · {c.client}
                </p>
                <p
                  className="mt-2 eyebrow"
                  style={{ color: "rgba(26,26,26,0.55)" }}
                >
                  {c.type}
                </p>
              </div>
            </div>

            {/* right body */}
            <div className="md:col-span-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <p
                    className="eyebrow mb-3"
                    style={{ color: "rgba(26,26,26,0.55)" }}
                  >
                    The challenge
                  </p>
                  <p className="text-[0.95rem] md:text-base leading-relaxed">
                    {c.challenge}
                  </p>
                </div>
                <div>
                  <p
                    className="eyebrow mb-3"
                    style={{ color: "rgba(26,26,26,0.55)" }}
                  >
                    What I did
                  </p>
                  <p className="text-[0.95rem] md:text-base leading-relaxed">
                    {c.role}
                  </p>
                </div>
              </div>

              {c.id === "03" && (
                <figure className="mt-10" data-testid={`case-${c.id}-video`}>
                  <div
                    className="relative w-full overflow-hidden"
                    style={{
                      aspectRatio: "16 / 9",
                      border: "0.5px solid rgba(26,26,26,0.35)",
                      backgroundColor: "#1A1A1A",
                    }}
                  >
                    <iframe
                      src="https://www.youtube.com/embed/D5zlW6XMeIk?rel=0&modestbranding=1"
                      title="Fi IPL Campaign"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 0 }}
                      data-testid={`case-${c.id}-video-iframe`}
                    />
                  </div>
                </figure>
              )}

              {/* stats row */}
              <ul
                className="mt-10 pt-8 grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4"
                style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
              >
                {c.stats.map((s) => (
                  <StatBlock key={s.l} value={s.v} label={s.l} />
                ))}
              </ul>

              {c.quote && (
                <div className="mt-10">
                  <blockquote
                    className="serif-display leading-[1.15] max-w-[36ch]"
                    style={{ fontSize: "clamp(1.25rem, 2.1vw, 1.65rem)" }}
                  >
                    <span className="serif-italic">&ldquo;</span>
                    {c.quote}
                    <span className="serif-italic">&rdquo;</span>
                  </blockquote>
                  {c.attribution && (
                    <p
                      className="mt-3 eyebrow"
                      style={{ color: "rgba(26,26,26,0.6)" }}
                    >
                      — {c.attribution}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-10 flex flex-wrap items-baseline gap-x-10 gap-y-4">
                <Link
                  to="/contact"
                  data-testid={`case-${c.id}-link`}
                  className="font-serif text-lg md:text-xl editorial-link"
                >
                  <span className="serif-italic">Ask for the full case study</span> →
                </Link>
                {c.liveUrl && (
                  <a
                    href={c.liveUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`case-${c.id}-live`}
                    className="font-serif text-lg md:text-xl editorial-link"
                  >
                    <span className="serif-italic">{c.liveUrlLabel || "View live"}</span> →
                  </a>
                )}
              </div>
            </div>
            </div>
          </article>
        ))}
      </section>

    </div>
  );
}

function FeaturedBlock({ label, body, className = "md:col-span-6" }) {
  return (
    <div className={className}>
      <p className="eyebrow mb-4" style={{ color: "rgba(26,26,26,0.55)" }}>
        {label}
      </p>
      <p className="text-base md:text-lg leading-relaxed">{body}</p>
    </div>
  );
}

function StatBlock({ value, label, large }) {
  return (
    <li>
      <p
        className={`serif-display leading-none ${large ? "text-4xl md:text-5xl lg:text-6xl" : "text-2xl md:text-3xl"}`}
      >
        {value}
      </p>
      <p
        className="mt-3 text-[10px] font-sans font-medium uppercase tracking-[0.2em] leading-snug"
        style={{ color: "rgba(26,26,26,0.6)" }}
      >
        {label}
      </p>
    </li>
  );
}
